import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import { alpha, styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dompurify from 'dompurify';

import { classes } from './styles';
import { BLACK, SCOUT_TEAL, WHITE } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';
import { FormTextField, MultilineFormTextField } from '../CustomUIElements/FormTextField';
import { SingleDatePicker } from '../CustomUIElements/SingleDatePicker';

export interface IApplicantRequisition {
  id: number;
  question: string;
  answer:
    | {
        id: number;
        name: string;
      }
    | IFieldCheckbox[];
  type: string;
  field_choices: IFieldChoice[];
}

export interface IFieldChoice {
  id: number;
  name: string;
  position: number;
}

export interface IFieldCheckbox {
  id: number;
  name: string;
}

const SelectInput = styled(InputBase)(() => ({
  'label + &': {
    marginTop: '24px'
  },
  '& .MuiInputBase-input': {
    borderRadius: '6px',
    position: 'relative',
    backgroundColor: WHITE,
    color: '#333333',
    border: '1px solid #DDDDDD',
    fontSize: '16px',
    padding: '6px 10px',
    margin: '3px 0px',
    fontFamily: 'Source Sans Pro, sans-serif',
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
      borderColor: theme.palette.primary.main
    }
  },
  '& .MuiInputBase-input:focus': {
    backgroundColor: WHITE,
    borderRadius: '6px'
  },
  '& .MuiSvgIcon-root': {
    color: '#AAAAAA',
    marginRight: '10px',
    transition: 'all 0.16s ease-in-out'
  }
}));

const menuItemStyle = {
  fontFamily: 'Source Sans Pro, sans-serif',
  color: '#333333',
  maxHeight: '200px'
};

const radioStyle = {
  color: '#DDDDDD',
  '&.Mui-checked': {
    color: theme.palette.action.main
  },
  '& .MuiSvgIcon-root': {
    fontSize: '22px'
  }
};

const labelStyle = {
  '& .MuiTypography-root': {
    color: `${BLACK} !important`,
    fontSize: '16px',
    marginLeft: '2px',
    fontFamily: 'Source Sans Pro, sans-serif'
  }
};

const checkboxStyle = {
  color: '#DDDDDD',
  '&.Mui-checked': {
    color: theme.palette.action.main
  },
  '& .MuiSvgIcon-root': {
    fontSize: '22px'
  }
};

const buttonStyle = {
  fontFamily: 'Source Sans Pro',
  fontSize: '12px',
  fontWeight: 'bold',
  textTransform: 'none',
  color: SCOUT_TEAL,
  backgroundColor: '#5BC4BF12',
  borderRadius: '6px',
  boxShadow: 'none',
  marginLeft: '20px',
  '&:hover': {
    backgroundColor: '#4da8a412',
    boxShadow: 'none'
  }
};

const RequisitionInfoViewer = ({
  requisition,
  index,
  requisitionInfos,
  setRequisitionInfos
}: {
  requisition: IApplicantRequisition;
}) => {
  const updateAnswer = (answerIndex: number, value: string | number) => {
    const answers = [...requisitionInfos];
    answers[answerIndex].answer = value;
    setRequisitionInfos(answers);
  };

  const updateCheckbox = (answerIndex: number, value: number) => {
    const i = requisitionInfos[answerIndex].answer.indexOf(value);
    if (i < 0) {
      const answers = [...requisitionInfos];
      answers[answerIndex].answer.push(value);
      setRequisitionInfos(answers);
    } else {
      requisitionInfos[answerIndex].answer.splice(i, 1);
      setRequisitionInfos(requisitionInfos);
    }
  };

  switch (requisition.type) {
    case 'RadioButtonField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <RadioGroup
            name="radio-buttons-group"
            onChange={(e) => updateAnswer(index, e.target.value)}
            sx={{ padding: '8px 0px', flexDirection: 'column' }}
            value={requisition.answer}
          >
            {requisition.field_choices
              .map((choice) => ({
                label: choice.name,
                value: choice.id
              }))
              .map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={<Radio sx={radioStyle} />}
                  label={option.label}
                  sx={labelStyle}
                  value={option.value}
                />
              ))}
          </RadioGroup>
        </Box>
      );
    case 'CheckBoxField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <Box sx={{ right: '12px' }}>
            <FormGroup sx={{ padding: '8px 0px', flexDirection: 'column' }}>
              {requisition.field_choices
                .map((choice) => ({
                  label: choice.name,
                  value: choice.id
                }))
                .map((option) => (
                  <FormControlLabel
                    key={option.value}
                    label={option.label}
                    sx={labelStyle}
                    control={
                      <Checkbox
                        defaultChecked={requisition.answer.includes(option.value)}
                        name={option.label}
                        onChange={() => updateCheckbox(index, option.value)}
                        sx={checkboxStyle}
                        value={option.value}
                      />
                    }
                  />
                ))}
            </FormGroup>
          </Box>
        </Box>
      );
    case 'TextField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <FormTextField
            onChange={(e) => updateAnswer(index, e.target.value)}
            styles={{ width: '290px' }}
            value={requisition.answer}
          />
        </Box>
      );
    case 'TextAreaField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <MultilineFormTextField
            onChange={(e) => updateAnswer(index, e.target.value)}
            styles={{ minWidth: '300px', maxWidth: '600px' }}
            value={requisition.answer}
          />
        </Box>
      );
    case 'SelectField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <Select
            input={<SelectInput />}
            onChange={(e) => updateAnswer(index, e.target.value)}
            sx={{ width: '290px' }}
            value={requisition.answer}
          >
            {requisition.field_choices
              .map((choice) => ({
                label: choice.name,
                value: choice.id
              }))
              .map((option) =>
                typeof option === 'string' ? (
                  <MenuItem key={option} sx={menuItemStyle} value={option}>
                    {option}
                  </MenuItem>
                ) : (
                  <MenuItem key={option.value} sx={menuItemStyle} value={option.value}>
                    {option.label}
                  </MenuItem>
                )
              )}
          </Select>
        </Box>
      );
    case 'DateField': {
      const { answer } = requisition;
      let dateValue: string | Date | null;
      if (typeof answer === 'string' || answer instanceof Date || answer === null) {
        dateValue = answer;
      } else if (Array.isArray(answer) && answer.length > 0) {
        dateValue = new Date(NaN);
      } else if (answer === undefined || Array.isArray(answer)) {
        dateValue = new Date();
      } else {
        const today = new Date();
        dateValue = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      }
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <Box sx={{ marginTop: '10px', ...classes.formDateWrapper }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <SingleDatePicker
                disabled={false}
                value={dateValue}
                onChange={(date) =>
                  updateAnswer(index, date?.isValid() ? date.format('YYYY-MM-DD') : 'Invalid date')
                }
              />
            </LocalizationProvider>
          </Box>
        </Box>
      );
    }
    case 'AvailabilityField':
    case 'CsvDataLookupField':
      return (
        <Box sx={{ marginTop: '20px' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(requisition.question) }}
            sx={classes.questionLabel}
          />
          <Box sx={{ marginTop: '10px', ...classes.formDateWrapper }}>
            <Select
              disabled
              input={<SelectInput />}
              onChange={{}}
              sx={{ width: '290px' }}
              value={requisition.answer[0] === 'existing' ? 'Existing Position' : 'New Position'}
            >
              {['Existing Position', 'New Position'].map((option) => (
                <MenuItem key={option} sx={menuItemStyle} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ marginTop: '10px', ...classes.questionLabel }}>
              {requisition.answer[0] === 'existing'
                ? 'Enter Existing Position Number'
                : 'Enter New Position Number'}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormTextField
                draggable
                onChange={{}}
                required={false}
                styles={{ width: '290px' }}
                value={requisition.answer[1]}
              />
              <Button disabled id="csv-look-up-button" onClick={{}} sx={buttonStyle}>
                Look up
              </Button>
            </Box>
          </Box>
        </Box>
      );
    case 'JobReferenceLookupField':
      return null;
    default:
      return (
        <Box>
          <Box>{requisition.question}</Box>
          Invalid question type
        </Box>
      );
  }
};

export default RequisitionInfoViewer;
